import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PrescribedDrug,
  PrescriptionStatus,
  ProtocolMedicationOption,
  ProtocolName,
  ProtocolService,
  useCurrentProtocol,
  useMedsRecommendation,
  useProtocolItemOptions,
  useUpsertPrescription
} from '@/modules/protocol';
import { useErrorDialog } from '@ianesthesie-kit/redux';
import {
  ConsultationStatCode,
  GroupedConsultationStats,
  useConsultationStatsQuery,
  useCurrentPatient
} from '@/modules/surgicalEvent';
import { usePrevious, useTranslator } from '@ianesthesie-kit/core';
import isEqual from 'lodash/fp/isEqual';
import { useQueryForCurrentPatient } from '@/hooks';

export type SelectedDrug = Partial<PrescribedDrug> &
  Pick<PrescribedDrug, 'name' | 'orderIndex'>;

export const useCurrentIdlePrescription = () => {
  const translate = useTranslator();
  const { showErrorDialog } = useErrorDialog();
  const { data: patient, isLoading: patientLoading } = useCurrentPatient();
  const { currentData: stats, isLoading: statsLoading } =
    useQueryForCurrentPatient<GroupedConsultationStats>({
      useQuery: useConsultationStatsQuery
    });
  const { data: protocol, isLoading: protocolLoading } = useCurrentProtocol(
    ProtocolName.GENERIC_PRESCRIPTION
  );

  const idlePrescription = useMemo(() => {
    const presc = protocol?.items?.find(
      (pr) => pr.status === PrescriptionStatus.IDLE
    );
    if (!presc) {
      return presc;
    }

    return {
      ...presc,
      prescribedDrugs: presc.prescribedDrugs ?? []
    };
  }, [protocol?.items]);
  const idlePrescriptionDrugs = idlePrescription?.prescribedDrugs;

  const recommendMeds = useMedsRecommendation(
    ProtocolName.GENERIC_PRESCRIPTION
  );
  const initialRecommendations = useMemo(() => {
    if (!patient) {
      return [];
    }
    return recommendMeds({
      age: patient.age,
      weight: stats?.[ConsultationStatCode.WEIGHT_KG]?.value
    });
  }, [patient, recommendMeds, stats]);

  const { data: globalOptions, isLoading: globalOptsLoading } =
    useProtocolItemOptions({
      protocol: ProtocolName.GENERIC_PRESCRIPTION
    });

  const selectedDrugs: SelectedDrug[] = useMemo(() => {
    const translateDosageAndComments = (opt?: ProtocolMedicationOption) => {
      if (opt?.code) {
        return translate(
          `doctor-dashboard.protocol.medication.defaultDosageAndComment.${opt.code}`
        );
      }
      return undefined;
    };

    if (idlePrescriptionDrugs) {
      return idlePrescriptionDrugs.map((dr) => {
        if (dr.dosageAndComments || !globalOptions) {
          return dr;
        }

        const option = globalOptions.find(
          (opt) => opt.nameWithDosage === dr.name
        );
        return {
          ...dr,
          dosageAndComments: translateDosageAndComments(option)
        };
      });
    }

    if (protocol?.items?.length) {
      return [];
    }

    return initialRecommendations.map((opt, i) => ({
      name: opt.nameWithDosage,
      orderIndex: i,
      dosageAndComments: translateDosageAndComments(opt)
    }));
  }, [
    globalOptions,
    idlePrescriptionDrugs,
    initialRecommendations,
    protocol?.items?.length,
    translate
  ]);

  const [notSelectedMedOptions, setNotSelectedMedOptions] = useState<
    ProtocolMedicationOption[]
  >([]);
  const prevOpts = usePrevious(notSelectedMedOptions);

  useEffect(() => {
    const selectedDrugNames = new Set(
      selectedDrugs?.map((dr) => dr.name) ?? []
    );

    const recommendedDrugCodes = new Set(
      initialRecommendations?.map((opt) => opt.code) ?? []
    );
    const deselectedRecommendedDrugCodes = initialRecommendations?.filter(
      (opt) => !selectedDrugNames.has(opt.nameWithDosage)
    );

    const opts = (globalOptions ?? [])
      .filter((opt) => !recommendedDrugCodes.has(opt.code))
      .filter((opt) => !selectedDrugNames.has(opt.nameWithDosage))
      .concat(deselectedRecommendedDrugCodes)
      .sort(ProtocolService.sortGenericPrescriptionOptions);

    if (!isEqual(prevOpts, opts)) {
      setNotSelectedMedOptions(opts);
    }
  }, [globalOptions, initialRecommendations, prevOpts, selectedDrugs]);

  const [upsertPrescription] = useUpsertPrescription();

  const onAdd = useCallback(
    (med?: ProtocolMedicationOption | string) => {
      if (!patient?.id) {
        return Promise.reject('Invalid arguments');
      }
      const newDrug = {
        name: typeof med === 'string' ? med : med?.nameWithDosage ?? ''
      };

      return upsertPrescription({
        patientId: patient.id,
        prescriptionId: idlePrescription?.id,
        drugs: [...selectedDrugs, newDrug]
      })
        .unwrap()
        .catch(showErrorDialog);
    },
    [
      idlePrescription?.id,
      patient?.id,
      selectedDrugs,
      showErrorDialog,
      upsertPrescription
    ]
  );

  const onUpdate = useCallback(
    (drugsOrComments: SelectedDrug[] | string) => {
      if (
        !patient?.id ||
        drugsOrComments === undefined ||
        drugsOrComments === null
      ) {
        return Promise.reject('Invalid arguments');
      }
      const generalComments =
        typeof drugsOrComments === 'string'
          ? drugsOrComments
          : idlePrescription?.generalComments;
      const drugs = Array.isArray(drugsOrComments)
        ? drugsOrComments
        : idlePrescription?.prescribedDrugs ?? selectedDrugs ?? [];

      return upsertPrescription({
        patientId: patient.id,
        prescriptionId: idlePrescription?.id,
        generalComments,
        drugs
      })
        .unwrap()
        .catch(showErrorDialog);
    },
    [
      idlePrescription?.generalComments,
      idlePrescription?.id,
      idlePrescription?.prescribedDrugs,
      patient?.id,
      selectedDrugs,
      showErrorDialog,
      upsertPrescription
    ]
  );

  const onRemove = useCallback(
    (drug: SelectedDrug) => {
      if (!patient?.id || !drug) {
        return Promise.reject('Invalid arguments');
      }
      return upsertPrescription({
        patientId: patient.id,
        prescriptionId: idlePrescription?.id,
        drugs: selectedDrugs.filter((d) =>
          d.id ? d.id !== drug.id : d.name !== drug.name
        )
      })
        .unwrap()
        .catch(showErrorDialog);
    },
    [
      idlePrescription?.id,
      patient?.id,
      selectedDrugs,
      showErrorDialog,
      upsertPrescription
    ]
  );

  return {
    isLoading:
      patientLoading || statsLoading || protocolLoading || globalOptsLoading,
    idlePrescription,
    notSelectedMedOptions,
    selectedDrugs,
    onAdd,
    onUpdate,
    onRemove,
    isEmpty: !selectedDrugs?.length
  };
};
